import { PARTICIPANT_ACTOR_KEY } from '@dadohr/shared/domainTypes/experience';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import styled from '@emotion/styled';
import { orderBy, sortBy, truncate } from 'lodash';
import { UserIcon, UsersIcon } from 'lucide-react';
import { useMemo } from 'react';
import { ColoredChip } from '../../../../../../../components/ColoredChip';
import {
  ItemSorters,
  RowsRenderer,
  RowsRendererHeadProps,
  ROW_HEIGHTS,
  useSortQueryParam
} from '../../../../../../../components/GroupableList';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { useStringQueryParam } from '../../../../../../../routes';
import { toFullName } from '../../../../../../../services/user';
import { ROUTES_V2 } from '../../../../../../routes';
import { PlainDate } from '../../../../Experience/components/Tasks';
import { InboxNavItemKey } from '../../../components/Nav/InboxNav';
import { InboxTableMode, MessageGroup } from '../types';
import { TASK_GROUP_TABLE_SORT_QUERY_PARAM } from './TaskGroupTable';
import { WithTokenPill } from './WithTokenPill';

// TODO - this is virtually identical to TaskGroupTable and could be unified

type ColumnName = 'msg' | 'experience' | 'received';
type D = MessageGroup;
type OtherProps = {
  userAccessToken: string;
  mode: InboxTableMode;
  returnTo: string;
  selectedGroup?: InboxNavItemKey;
  selectedUsers?: string[] | null;
  selectedTemplates?: string[] | null;
  tsort?: string;
};
type Column = IColumn<D, ColumnName, OtherProps>;

const getLastSendDate = (g: MessageGroup): Timestamp | null => {
  const sorted = sortBy(g.messages, (t) => -t.sendAt.toMillis());
  return sorted[0]?.sendAt || null;
};

const GroupTitleGrid = styled('div')((p) => ({
  display: 'grid',
  width: '100%',
  gridTemplateColumns: 'max-content 55px 1fr',
  gridColumnGap: p.theme.spacing(1),
  alignItems: 'center',
  whiteSpace: 'nowrap'
}));

const ICON_SIZE = 20;
const GroupTitle = ({ g, mode }: { g: MessageGroup; mode: InboxTableMode }) => {
  const { t } = useUnsafeTranslation();
  const isSingle = g.messages.length === 1;
  const label =
    isSingle && g.messages[0].actorKey === PARTICIPANT_ACTOR_KEY
      ? t('Me')
      : g.messages.length;

  return (
    <GroupTitleGrid>
      {isSingle ? (
        <UserIcon size={ICON_SIZE} />
      ) : (
        <UsersIcon size={ICON_SIZE} />
      )}
      <ColoredChip color="gray" mode="light">
        {label}
      </ColoredChip>
      <WithTokenPill
        d={isSingle ? g.messages[0].msgSubjectInterpolated : g.msgTitle}
      />
    </GroupTitleGrid>
  );
};

const EXPERIENCE_COLUMN: Column = {
  key: 'experience',
  head: () => 'Experience',
  cell: (d) => <WithTokenPill d={d.templateTitle} />,
  align: 'left',
  sortable: true,
  width: 150,
  flexGrow: 1,
  noWrap: true
};

const RECEIVED_COLUMN: Column = {
  key: 'received',
  head: () => 'Received',
  cell: (d) => {
    const sentAt = getLastSendDate(d);
    return sentAt ? <PlainDate ts={sentAt} /> : <div />;
  },
  sortable: true,
  align: 'right',
  width: 120,
  flexGrow: 0
};

const COLUMNS_WITH_SUPPORTING_ACTOR: Column[] = [
  {
    key: 'msg',
    head: () => 'Message',
    cell: (d, o) => <GroupTitle g={d} mode={o.mode} />,
    align: 'left',
    width: 300,
    sortable: true,
    flexGrow: 5,
    noWrap: true
  },
  EXPERIENCE_COLUMN,
  RECEIVED_COLUMN
];

const COLUMNS_PARTICIPANT_ONLY: Column[] = [
  {
    key: 'msg',
    head: () => 'Message',
    // PARTICIPANT view: variables ALWAYS resolve (product spec 2026-07-13),
    // including collapsed multi-message groups — every message in a
    // participantOnly group belongs to the same participant, so its
    // interpolated subject is valid for the whole row. (Humanized `msgTitle`
    // remains the fallback only if interpolation is absent on legacy payloads.)
    cell: (d) => (
      <WithTokenPill
        d={d.messages[0]?.msgSubjectInterpolated ?? d.msgTitle}
      />
    ),
    align: 'left',
    width: 300,
    sortable: true,
    flexGrow: 5,
    noWrap: true
  },
  EXPERIENCE_COLUMN,
  RECEIVED_COLUMN
];

const COLUMNS_SINGLE_USER: Column[] = [
  {
    key: 'msg',
    head: () => 'Message',
    cell: (d) => {
      const msg = d.messages[0];
      return (
        <FlexContainer style={{ whiteSpace: 'nowrap' }}>
          <ColoredChip color="gray" mode="light">
            {truncate(toFullName(msg.participant), { length: 30 })}
          </ColoredChip>

          <WithTokenPill d={msg.msgSubjectInterpolated} />
        </FlexContainer>
      );
    },
    align: 'left',
    width: 300,
    sortable: true,
    flexGrow: 4
  },
  EXPERIENCE_COLUMN,
  RECEIVED_COLUMN
];

export const MESSAGE_GROUP_TABLE_SORT_GETTERS: Record<
  string,
  (d: MessageGroup) => string | number
> = {
  msg: (d) => d.msgTitle,
  experience: (d) => d.templateTitle,
  received: (d) => getLastSendDate(d)?.toMillis() || Infinity
};

const SORTERS: ItemSorters<D, OtherProps> = {
  msg: {
    key: 'msg',
    items: { sort: (d) => MESSAGE_GROUP_TABLE_SORT_GETTERS.msg(d), dir: 'asc' }
  },
  experience: {
    key: 'experience',
    items: {
      sort: (d) => MESSAGE_GROUP_TABLE_SORT_GETTERS.experience(d),
      dir: 'asc'
    }
  },

  received: {
    key: 'received',
    items: {
      sort: (d) => MESSAGE_GROUP_TABLE_SORT_GETTERS.received(d),
      dir: 'desc'
    }
  }
};

const MESSAGE_GROUP_TABLE_DEFAULT_SORTER = SORTERS.received;

export const sortMessageGroups = (
  messageGroups: MessageGroup[],
  key: string | null | undefined,
  dir: 'asc' | 'desc'
) => {
  const sorter =
    MESSAGE_GROUP_TABLE_SORT_GETTERS[key || ''] ||
    MESSAGE_GROUP_TABLE_SORT_GETTERS[MESSAGE_GROUP_TABLE_DEFAULT_SORTER.key];
  return orderBy(messageGroups, (d) => sorter(d), dir);
};

const ROW_TO_KEY = (d: D) => d.templateId + '|' + d.msgTemplateId;

const ROW_TO_HREF = (d: D, o: OtherProps) => {
  if (d.messages.length === 1) {
    const msg = d.messages[0];
    return ROUTES_V2.inboxMessage.url(
      o.userAccessToken,
      msg.msgTemplateId,
      msg.instanceAccessToken,
      {
        returnTo: o.returnTo,
        g: o.selectedGroup,
        u: o.selectedUsers,
        t: o.selectedTemplates,
        tsort: o.tsort
      }
    );
  }
  return ROUTES_V2.inboxMessages.url(o.userAccessToken, d.msgTemplateId, {
    returnTo: o.returnTo,
    g: o.selectedGroup,
    u: o.selectedUsers,
    t: o.selectedTemplates,
    tsort: o.tsort
  });
};

const HEAD_PROPS: RowsRendererHeadProps = {
  transparent: true,
  noTopPadding: true
};

export const MessageGroupTable = ({
  groups,
  mode,
  userAccessToken,
  returnTo,
  selectedGroup,
  selectedTemplates,
  selectedUsers
}: {
  groups: MessageGroup[];
  mode: InboxTableMode;
  userAccessToken: string;
  returnTo: string;
  selectedGroup?: InboxNavItemKey;
  selectedTemplates?: string[] | null;
  selectedUsers?: string[] | null;
}) => {
  const [[sorter, dir], setSort] = useSortQueryParam(
    TASK_GROUP_TABLE_SORT_QUERY_PARAM,
    SORTERS
  );
  const [rawSortParam] = useStringQueryParam(
    TASK_GROUP_TABLE_SORT_QUERY_PARAM,
    ''
  );

  const otherProps = useMemo<OtherProps>(() => {
    return {
      userAccessToken,
      mode,
      returnTo,
      selectedGroup,
      selectedUsers,
      selectedTemplates,
      tsort: rawSortParam
    };
  }, [
    mode,
    userAccessToken,
    returnTo,
    selectedGroup,
    selectedUsers,
    selectedTemplates,
    rawSortParam
  ]);
  const columns =
    mode === 'participantOnly'
      ? COLUMNS_PARTICIPANT_ONLY
      : mode === 'singleUser'
      ? COLUMNS_SINGLE_USER
      : COLUMNS_WITH_SUPPORTING_ACTOR;
  return (
    <RowsRenderer
      fullWidth
      variant="contained"
      columns={columns}
      rows={groups}
      rowToKey={ROW_TO_KEY}
      rowToHref={ROW_TO_HREF}
      sorter={sorter || MESSAGE_GROUP_TABLE_DEFAULT_SORTER}
      sortDirection={dir}
      renderHead={true}
      headProps={HEAD_PROPS}
      onHeadClick={(c, d) =>
        setSort([SORTERS[c.key] || MESSAGE_GROUP_TABLE_DEFAULT_SORTER, d])
      }
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
    />
  );
};
